import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, RefreshCw, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit',
        }) + ' CT';
    } catch { return iso; }
};

/**
 * MajikBonus API error viewer. Pulls the raw vendor-error JSON from
 * db.majik_audit so admins can copy-paste the exact response the vendor
 * asks for in support tickets.
 */
export const MajikErrorsPanel = () => {
    const [errors, setErrors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hours, setHours] = useState(24);
    const [expanded, setExpanded] = useState(new Set());

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/majik/errors?hours=${hours}&limit=50`);
            setErrors(data.errors || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to load errors');
        }
        setLoading(false);
    }, [hours]);

    useEffect(() => { load(); }, [load]);

    const copyJson = (e) => {
        const payload = e.response || {
            error: e.error,
            raw: e.raw,
            method: e.method,
        };
        try {
            navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
            toast.success('Copied — paste into MajikBonus support ticket');
        } catch {
            toast.error('Copy failed — highlight and copy manually');
        }
    };

    const toggle = (idx) => {
        setExpanded((s) => {
            const n = new Set(s);
            if (n.has(idx)) n.delete(idx); else n.add(idx);
            return n;
        });
    };

    // Group by vendor_code for quick pattern-spotting
    const grouped = errors.reduce((acc, e) => {
        const k = e.vendor_code || e.error || 'unknown';
        acc[k] = (acc[k] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="card-dark p-5 mt-5" style={{ borderLeft: '3px solid #FF3B30' }} data-testid="majik-errors-panel">
            <div className="flex items-start gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-[#FF3B30] shrink-0 mt-0.5" />
                <div className="flex-1">
                    <h3 className="text-lg font-bold text-white">MajikBonus API errors</h3>
                    <p className="text-xs text-[#A0AAB2]">
                        Exact vendor JSON to paste into their support tickets. Every failed call is captured here.
                    </p>
                </div>
                <select
                    value={hours}
                    onChange={(e) => setHours(Number(e.target.value))}
                    className="h-9 px-3 rounded-md bg-black border border-white/10 text-white text-sm"
                    data-testid="majik-errors-hours"
                >
                    <option value={1}>Last 1h</option>
                    <option value={6}>Last 6h</option>
                    <option value={24}>Last 24h</option>
                    <option value={72}>Last 3d</option>
                    <option value={168}>Last 7d</option>
                </select>
                <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="majik-errors-refresh">
                    <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </Button>
            </div>

            {Object.keys(grouped).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {Object.entries(grouped).map(([code, count]) => (
                        <span key={code} className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded bg-[#FF3B30]/10 text-[#FF6B6B] border border-[#FF3B30]/40" data-testid={`majik-errors-code-${code}`}>
                            {code} · {count}
                        </span>
                    ))}
                </div>
            )}

            {errors.length === 0 ? (
                <div className="text-center text-[#A0AAB2] py-6 border border-dashed border-white/10 rounded-lg" data-testid="majik-errors-empty">
                    {loading ? 'Loading…' : 'No MajikBonus errors in this window. 🎉'}
                </div>
            ) : (
                <div className="space-y-2">
                    {errors.map((e, i) => (
                        <div key={i} className="border border-white/10 rounded-lg bg-black/40 overflow-hidden" data-testid={`majik-error-row-${i}`}>
                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => toggle(i)}
                                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); toggle(i); } }}
                                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/[0.03] text-left cursor-pointer"
                            >
                                {expanded.has(i) ? <ChevronDown className="w-4 h-4 text-[#A0AAB2]" /> : <ChevronRight className="w-4 h-4 text-[#A0AAB2]" />}
                                <span className="text-xs font-mono text-[#A0AAB2]">{fmtTime(e.created_at)}</span>
                                <span className="text-xs text-white font-semibold">{e.method}</span>
                                <span className="text-xs uppercase tracking-wider text-[#FF6B6B] font-bold">
                                    {e.vendor_code || (e.error ? 'network' : 'unknown')}
                                </span>
                                <span className="text-xs text-[#A0AAB2] truncate flex-1">{e.vendor_message || e.error}</span>
                                <button
                                    type="button"
                                    onClick={(ev) => { ev.stopPropagation(); copyJson(e); }}
                                    className="ml-2 text-xs px-2 py-1 rounded bg-[#FFD700]/10 text-[#FFD700] hover:bg-[#FFD700]/20 flex items-center gap-1"
                                    data-testid={`majik-error-copy-${i}`}
                                >
                                    <Copy className="w-3 h-3" /> Copy JSON
                                </button>
                            </div>
                            {expanded.has(i) && (
                                <div className="px-3 py-2 border-t border-white/10 bg-black/60 space-y-2">
                                    {e.vendor_trace_id && (
                                        <div className="text-xs">
                                            <span className="text-[#A0AAB2]">Trace ID: </span>
                                            <span className="font-mono text-white">{e.vendor_trace_id}</span>
                                        </div>
                                    )}
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold mb-1">Request</div>
                                        <pre className="text-[11px] bg-black rounded p-2 overflow-x-auto text-[#c8c8c8]">{JSON.stringify(e.request, null, 2)}</pre>
                                    </div>
                                    <div>
                                        <div className="text-[10px] uppercase tracking-wider text-[#A0AAB2] font-bold mb-1">Response (paste this to support)</div>
                                        <pre className="text-[11px] bg-black rounded p-2 overflow-x-auto text-[#FFD700]">{JSON.stringify(e.response || { error: e.error, raw: e.raw }, null, 2)}</pre>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MajikErrorsPanel;
